/**
 * Which side of an NID card is this — front or back?
 *
 * Runs on the ALREADY-RECTIFIED CR80 crop, so every landmark sits in a known
 * box and no ML model (or network call) is needed.
 *
 * The rule that makes this survive real uploads — a card on a table, shot at an
 * angle, under a bulb, half in shadow, blurred and JPEG-mushed — is that **every
 * cue is measured against the card's own statistics**, never against a fixed
 * 0-255 value:
 *
 *   · Colour is read after a white-patch balance, so the uniform yellow of a
 *     tungsten bulb or the blue of shade does not make the (nearly monochrome)
 *     back look as colourful as the front.
 *   · Tone is read against the card's own paper white, so an under-exposed or
 *     blurred card is compared with itself rather than with a bright one.
 *   · Box cues are DIFFERENCES against the same measurement over the whole card.
 *     Blur, shadow and any leftover cast move every pixel together; "this corner
 *     is redder than the rest of the card" survives that, "this corner is red"
 *     does not.
 *
 * The cues themselves, measured on real photos of a real card:
 *
 *   front · a photograph in the portrait box — a spread of mid tones where the
 *           rest of the card is paper and ink. This is the strongest single cue.
 *   front · the red flag emblem in the top-left corner.
 *   front · coloured ink overall: the green header, the red title and digits.
 *   back  · a barcode — a horizontal band whose rows flip between dark and light
 *           dozens of times each. Address text flips too, but only for the few
 *           rows of one line, so a band AVERAGE separates them while surviving
 *           the blur that kills a strict per-row threshold.
 *
 * Since a photo can be upside down, every corner cue is read at both half-turns
 * and the side is decided on the better one; the orientation is then decided by
 * which half-turn it was.
 *
 * Deliberately NOT used: skin-tone detection on the portrait. Phone shots of a
 * laminated card carry a strong colour cast, so every r>g>b skin rule reads zero
 * — which is exactly the kind of absolute test this file avoids.
 */

/** Long edge used for the cue pass — landmarks are large, detail is not needed. */
const WORK_W = 324;

/** Portrait photograph, front left. */
const PORTRAIT_BOX = { x0: 0.02, y0: 0.33, x1: 0.20, y1: 0.75 };
/** Red flag emblem, top-left of the front — inset, so a sliver of table in the
 *  corner of an imperfect crop cannot pass for the emblem. */
const FLAG_BOX = { x0: 0.025, y0: 0.05, x1: 0.155, y1: 0.27 };
const WHOLE_CARD = { x0: 0, y0: 0, x1: 1, y1: 1 };

/** Height of the sliding band the barcode search averages over. */
const BAND_FRAC = 0.09;

function toWorking(source) {
    const sw = source.naturalWidth || source.width;
    const sh = source.naturalHeight || source.height;
    if (!sw || !sh) return null;
    const w = Math.min(WORK_W, sw);
    const h = Math.max(1, Math.round((sh / sw) * w));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(source, 0, 0, w, h);
    try {
        return { data: ctx.getImageData(0, 0, w, h).data, w, h };
    } catch {
        return null; // tainted canvas
    }
}

/** Rotate an RGBA buffer half a turn. */
function rotate180(data, w, h) {
    const out = new Uint8ClampedArray(data.length);
    const n = w * h;
    for (let i = 0; i < n; i++) {
        const j = n - 1 - i;
        out[j * 4] = data[i * 4];
        out[j * 4 + 1] = data[i * 4 + 1];
        out[j * 4 + 2] = data[i * 4 + 2];
        out[j * 4 + 3] = data[i * 4 + 3];
    }
    return out;
}

const lumaAt = (d, i) => 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];

/**
 * Shrink the crop to the printed card inside it.
 *
 * Every box below is a fraction of the crop, so a quad that was drawn a little
 * wide — common when the card lies on a plain table and the detector locks onto
 * a shadow or a mat edge — slides the portrait and the emblem out of the boxes
 * that look for them. The card is the busy part of the crop: the margins that
 * carry no detail are trimmed off first.
 *
 * A trim is only taken when it leaves most of the crop intact; anything more
 * aggressive would be a second detector, and a wrong one would be worse than
 * the loose crop it replaced.
 */
function trimToContent(d, w, h) {
    const rows = new Float64Array(h);
    const cols = new Float64Array(w);
    for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
            const i = y * w + x;
            const g = Math.abs(lumaAt(d, i + 1) - lumaAt(d, i - 1))
                + Math.abs(lumaAt(d, i + w) - lumaAt(d, i - w));
            rows[y] += g;
            cols[x] += g;
        }
    }

    const span = (profile, n) => {
        const peak = percentile(profile, 0.9);
        if (peak <= 0) return [0, n];
        const floor = peak * 0.25;
        let a = 0;
        let b = n - 1;
        while (a < n && profile[a] < floor) a++;
        while (b > a && profile[b] < floor) b--;
        return [a, b + 1];
    };

    const [y0, y1] = span(rows, h);
    const [x0, x1] = span(cols, w);
    const kept = ((x1 - x0) / w) * ((y1 - y0) / h);
    if (kept < 0.5 || kept > 0.98) return { data: d, w, h };

    const nw = x1 - x0;
    const nh = y1 - y0;
    const out = new Uint8ClampedArray(nw * nh * 4);
    for (let y = 0; y < nh; y++) {
        const src = ((y + y0) * w + x0) * 4;
        out.set(d.subarray(src, src + nw * 4), y * nw * 4);
    }
    return { data: out, w: nw, h: nh };
}

const satAt = (d, i) => {
    const r = d[i * 4];
    const g = d[i * 4 + 1];
    const b = d[i * 4 + 2];
    const mx = r > g ? (r > b ? r : b) : (g > b ? g : b);
    const mn = r < g ? (r < b ? r : b) : (g < b ? g : b);
    return mx ? (mx - mn) / mx : 0;
};

/** The value at `p` of a sorted copy — used for paper white and channel gains. */
function percentile(values, p) {
    const sorted = Float64Array.from(values).sort();
    return sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor(sorted.length * p)))];
}

/**
 * White-patch balance: scale each channel so the card's bright pixels — its
 * paper — come out neutral. A per-channel percentile is used rather than a mean
 * over "bright" pixels, because a specular highlight clips to white in every
 * channel and would otherwise hide the cast it sits in.
 */
function whiteBalance(d, w, h) {
    const n = w * h;
    const channel = [new Float64Array(n), new Float64Array(n), new Float64Array(n)];
    for (let i = 0; i < n; i++) {
        channel[0][i] = d[i * 4];
        channel[1][i] = d[i * 4 + 1];
        channel[2][i] = d[i * 4 + 2];
    }
    const white = channel.map((c) => percentile(c, 0.85));
    const grey = (white[0] + white[1] + white[2]) / 3;
    if (grey < 12) return d; // a near-black frame has no white to balance on
    const gain = white.map((v) => (v > 8 ? grey / v : 1));
    const out = new Uint8ClampedArray(d.length);
    for (let i = 0; i < n; i++) {
        for (let c = 0; c < 3; c++) {
            const v = d[i * 4 + c] * gain[c];
            out[i * 4 + c] = v > 255 ? 255 : v;
        }
        out[i * 4 + 3] = 255;
    }
    return out;
}

/** The card's paper white, as this photo exposed it. */
function paperLevel(d, w, h) {
    const n = w * h;
    const v = new Float64Array(n);
    for (let i = 0; i < n; i++) v[i] = lumaAt(d, i);
    return Math.max(40, percentile(v, 0.85));
}

/** Fraction of pixels in a relative box satisfying `test(index)`. */
function fractionIn(d, w, h, box, test) {
    const x0 = Math.max(0, Math.round(box.x0 * w));
    const x1 = Math.min(w, Math.round(box.x1 * w));
    const y0 = Math.max(0, Math.round(box.y0 * h));
    const y1 = Math.min(h, Math.round(box.y1 * h));
    let hit = 0;
    let total = 0;
    for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
            total++;
            if (test(y * w + x)) hit++;
        }
    }
    return total ? hit / total : 0;
}

/**
 * The jumpiest horizontal band, and where it sits.
 *
 * Each row is binarised at its own midpoint and its dark/light flips counted;
 * the band score is the AVERAGE over a band ~9% of the card tall. The average is
 * what survives blur and JPEG: they lower every row's flip count at once, so a
 * fixed per-row threshold stops firing on a barcode that is still perfectly
 * visible, while a band of address text never averages as high as a barcode.
 */
function barcodeBand(d, w, h) {
    const flips = new Float64Array(h);
    for (let y = 0; y < h; y++) {
        let mn = 255;
        let mx = 0;
        for (let x = 0; x < w; x++) {
            const v = lumaAt(d, y * w + x);
            if (v < mn) mn = v;
            if (v > mx) mx = v;
        }
        if (mx - mn < 25) continue;
        const mid = (mn + mx) / 2;
        let n = 0;
        let prev = lumaAt(d, y * w) > mid;
        for (let x = 1; x < w; x++) {
            const cur = lumaAt(d, y * w + x) > mid;
            if (cur !== prev) n++;
            prev = cur;
        }
        flips[y] = n / w;
    }

    const band = Math.max(3, Math.round(h * BAND_FRAC));
    let best = 0;
    let bestEnd = band;
    let sum = 0;
    for (let y = 0; y < h; y++) {
        sum += flips[y];
        if (y >= band) sum -= flips[y - band];
        if (y >= band - 1 && sum / band > best) {
            best = sum / band;
            bestEnd = y;
        }
    }
    return { energy: best, centre: (bestEnd - band / 2) / h };
}

/**
 * Does this crop hold printed matter at all?
 *
 * When the quad detector locks onto the table instead of the card, the crop is a
 * smooth wash of one colour — and the cue differences below, being differences,
 * can still come out looking confident on it. Ink coverage and edge detail are
 * both near zero on such a crop and comfortably above these floors on every real
 * card face in the bench, so they gate the answer rather than feed it.
 */
function printedMatter(d, w, h, paper) {
    let ink = 0;
    let detail = 0;
    for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
            const i = y * w + x;
            if (lumaAt(d, i) < 0.55 * paper) ink++;
            detail += Math.abs(lumaAt(d, i + 1) - lumaAt(d, i - 1))
                + Math.abs(lumaAt(d, i + w) - lumaAt(d, i - w));
        }
    }
    const n = Math.max(1, (w - 2) * (h - 2));
    return { ink: ink / n, detail: detail / (n * paper) };
}

const MIN_INK = 0.02;
const MIN_DETAIL = 0.055;

/** Mid-tone fraction measured against the card's own paper, not against 0-255. */
function midToneIn(d, w, h, box, paper) {
    return fractionIn(d, w, h, box, (i) => {
        const v = lumaAt(d, i) / paper;
        return v > 0.28 && v < 0.78;
    });
}

/** Red-dominant fraction: the flag emblem's ink, whatever the exposure. */
const isFlagRed = (d) => (i) => {
    const r = d[i * 4];
    const g = d[i * 4 + 1];
    const b = d[i * 4 + 2];
    return r > 70 && r > g * 1.35 && r > b * 1.25;
};

/** Genuinely coloured ink, after the cast has been taken out. */
const isColoured = (d) => (i) => satAt(d, i) > 0.30 && lumaAt(d, i) > 40;

/** Corner cues for one orientation, each as a difference from the whole card. */
function orientationCues(d, w, h, paper) {
    const red = isFlagRed(d);
    const flagBox = fractionIn(d, w, h, FLAG_BOX, red);
    const flagAll = fractionIn(d, w, h, WHOLE_CARD, red);
    const portrait = midToneIn(d, w, h, PORTRAIT_BOX, paper);
    const cardMid = midToneIn(d, w, h, WHOLE_CARD, paper);
    return { flag: flagBox - flagAll, portrait: portrait - cardMid };
}

const sat = (v, ref) => Math.max(0, Math.min(1, v / ref));

/**
 * Classify a rectified NID crop.
 * @param {HTMLImageElement|HTMLCanvasElement} source rectified CR80 card image
 * @returns {{ side: 'front'|'back'|null, confidence: number, flip180: boolean, cues: object } | null}
 */
export function classifyCardSide(source) {
    if (typeof document === 'undefined') return null;
    const read = toWorking(source);
    if (!read) return null;
    const work = trimToContent(read.data, read.w, read.h);
    const { w, h } = work;

    const wb = whiteBalance(work.data, w, h);
    const paper = paperLevel(wb, w, h);

    const printed = printedMatter(wb, w, h, paper);
    if (printed.ink < MIN_INK || printed.detail < MIN_DETAIL) {
        return { side: null, confidence: 0, evidence: 0, flip180: false, cues: printed };
    }

    const up = orientationCues(wb, w, h, paper);
    const down = orientationCues(rotate180(wb, w, h), w, h, paper);
    const bar = barcodeBand(wb, w, h);
    const colour = fractionIn(wb, w, h, WHOLE_CARD, isColoured(wb));

    // Side is orientation-independent, so each corner cue counts at its better
    // half-turn; the portrait cue is what the front is really recognised by.
    const portrait = Math.max(up.portrait, down.portrait);
    const flag = Math.max(up.flag, down.flag);

    const front = 1.0 * sat(portrait, 0.20) + 0.8 * sat(flag, 0.08) + 0.4 * sat(colour, 0.12);
    // A card whose portrait box is QUIETER than the card as a whole is a back:
    // the back's left edge is paper, the front's is a photograph.
    const back = 1.2 * sat(-portrait, 0.05) + 1.0 * sat(bar.energy, 0.20);

    const cues = { portrait, flag, colour, bar: bar.energy, barCentre: bar.centre };
    // How much card-face evidence there is at all, either way. A crop of the
    // table, or of the blank notebook next to the card, scores near zero here —
    // which is what lets the detector re-rank its candidate quads by it.
    const total = front + back;
    if (total < 0.25) return { side: null, confidence: 0, evidence: total, flip180: false, cues };

    const side = front >= back ? 'front' : 'back';
    const confidence = Math.abs(front - back) / total;

    // Orientation: the front is pinned by its flag emblem and portrait, the back
    // by its barcode, which belongs in the lower half of the card.
    const pin = (c) => 0.8 * sat(c.flag, 0.08) + 1.0 * sat(c.portrait, 0.20);
    const flip180 = side === 'front'
        ? pin(down) > pin(up) + 0.12
        : bar.energy > 0.12 && bar.centre < 0.42;

    return { side, confidence, evidence: total, flip180, cues };
}

/** Above this margin the side call is trusted enough to pick the slot. */
export const MIN_SIDE_CONFIDENCE = 0.25;
